import React, { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import OptimizedImage from '@/components/common/OptimizedImage';

export interface HeroProps {
  title: string;
  subtitle?: string;
  description?: string;
  details?: string;
  primaryCTA?: {
    label: string;
    href: string;
    icon?: ReactNode;
  };
  secondaryCTA?: {
    label: string;
    href: string;
  };
  image?: {
    src: string;
    alt: string;
    width?: number;
    height?: number;
  };
  className?: string;
  backgroundClassName?: string;
  titleClassName?: string;
  subtitleClassName?: string;
  descriptionClassName?: string;
  detailsClassName?: string;
  imageContainerClassName?: string;
  children?: ReactNode;
}

const Hero = ({
  title,
  subtitle,
  description,
  details,
  primaryCTA,
  secondaryCTA,
  image,
  className,
  backgroundClassName = 'bg-gradient-to-r from-blue-50 to-indigo-100',
  titleClassName = 'text-4xl md:text-5xl font-bold mb-4 leading-tight text-blue-900',
  subtitleClassName = 'block text-3xl md:text-4xl mt-2 text-blue-700',
  descriptionClassName = 'text-xl mb-8 text-gray-700',
  detailsClassName = 'mb-8 max-w-lg text-gray-600',
  imageContainerClassName = 'bg-blue-100 rounded-3xl p-4 lg:p-8 shadow-2xl mb-4 lg:absolute lg:top-4 lg:right-4 lg:-rotate-3 lg:w-5/6',
  children,
}: HeroProps) => {
  return (
    <section className={cn('py-20 overflow-hidden', backgroundClassName)}>
      <div className="container mx-auto px-4">
        <div className={cn('flex flex-wrap items-center', className)}>
          <div className="w-full lg:w-1/2 mb-10 lg:mb-0">
            <h1 className={cn(titleClassName)}>
              {title}
              {subtitle && (
                <span className={cn(subtitleClassName)}>
                  {subtitle}
                </span>
              )}
            </h1>
            
            {description && (
              <p className={cn(descriptionClassName)}>
                {description}
              </p>
            )}
            
            {details && (
              <p className={cn(detailsClassName)}>
                {details}
              </p>
            )}
            
            {(primaryCTA || secondaryCTA) && (
              <div className="flex flex-wrap gap-4">
                {primaryCTA && (
                  <Button 
                    size="lg" 
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    asChild
                  >
                    <a href={primaryCTA.href}>
                      {primaryCTA.label}
                      {primaryCTA.icon && (
                        <span className="ml-2">{primaryCTA.icon}</span>
                      )}
                    </a>
                  </Button>
                )}
                
                {secondaryCTA && (
                  <Button 
                    variant="outline" 
                    size="lg"
                    asChild
                  >
                    <a href={secondaryCTA.href}>
                      {secondaryCTA.label}
                    </a>
                  </Button>
                )}
              </div>
            )}
            
            {children}
          </div>
          
          {image && (
            <div className="w-full lg:w-1/2 relative">
              <div className={cn(imageContainerClassName)}>
                <OptimizedImage 
                  src={image.src} 
                  alt={image.alt} 
                  className="rounded-xl shadow-lg w-full h-auto" 
                  width={image.width || 600}
                  height={image.height || 400}
                  loading="eager"
                  sizes="(max-width: 1024px) 100vw, 50vw"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Hero; 